import type { RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'

/**
 * The chat's routes (docs/migration/wp/messages PLAN.md §4) — they take over the inbox's paths and route names, so
 * the links of the other modules keep working: `inbox-all` is the chat, `inbox-new` too (the old "compose"; a
 * `?newMsg=1` changes nothing), `inbox-send` (`/messages/new/:recipientId`, candidates / employees / onboarding /
 * job offers "Send message") leads to the DM with that person, and `inbox-message` (`/messages/:msgid`, the
 * dashboards' unread messages) to the chat until the migrated messages can be found by their old id
 * (`legacy_message_id`, a later step). No software in the path: the chat is the workspace's, for every user type.
 * Legacy `/messages/inbox` and `/messages/archive` reach `/messages` through the generated redirects.
 * `/messages/call/:callId` is a call's own window (full layout, no app chrome; `?video=1` a video call, `?joined=1`
 * opened by the page that started it — composables/useCallWindow.ts).
 */
const crumb = { text: keys.t('messages.view.title.messages'), active: true }
const meta = { pageTitle: keys.t('messages.view.title.messages'), breadcrumb: [crumb] }
const MessagesView = () => import('./views/MessagesView.vue')
const CallView = () => import('./views/CallView.vue')

const routes: RouteRecordRaw[] = [
  { path: '/messages', name: 'inbox-all', component: MessagesView, meta },
  { path: '/messages/new', name: 'inbox-new', component: MessagesView, meta },
  {
    path: '/messages/c/:id(\\d+)',
    name: 'messages-conversation',
    component: MessagesView,
    props: (route) => ({ conversationId: Number(route.params.id) }),
    meta,
  },
  {
    path: '/messages/dm/:userId(\\d+)',
    name: 'messages-dm',
    component: MessagesView,
    props: (route) => ({ dmUserId: Number(route.params.userId) }),
    meta,
  },
  {
    path: '/messages/call/:callId(\\d+)',
    name: 'messages-call',
    component: CallView,
    props: (route) => ({
      callId: Number(route.params.callId),
      video: route.query.video === '1',
      joined: route.query.joined === '1',
    }),
    meta: { layout: 'full', pageTitle: keys.t('messages.call.title.call') },
  },
  {
    path: '/messages/new/:recipientId',
    name: 'inbox-send',
    // The query string and the hash are kept (a shared link's anchor, the route test's `?e2e=1#manifest`).
    redirect: (to) => ({
      name: 'messages-dm',
      params: { userId: String(to.params.recipientId) },
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: '/messages/:msgid(\\d+)',
    name: 'inbox-message',
    redirect: (to) => ({ name: 'inbox-all', query: to.query, hash: to.hash }),
  },
]

export default routes
